import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Col, UiBox, UiButton, UiImage, UiQuestionnaireLayout, UiSimpleGrid, UiText} from '~/components/elements';
import {ScrollToBottomButton} from '~/components/shared/ScrollToBottomButton';
import {BIRTH_CONTROL_TELECONSULTATION_KEYS} from '~/constants/medical-questionnaires';
import {useInput, useNotification} from '~/hooks';
import {setNextQuestionnaire, setQuestionnaireAnswer} from '~/redux/questionnaires/slice';
import {Relation} from '~/redux/questionnaires/types';
import {RootState} from '~/redux/root-reducer';
import {AppDispatch} from '~/redux/root-store';
import {spacing} from '~/utils/theme/spacing';

const Questionnaire8Screen = () => {
  const dispatch: AppDispatch = useDispatch();
  const {addMessage} = useNotification(dispatch);

  const currentAnswer = useSelector(
    (state: RootState) => state.questionnaires?.collectData[BIRTH_CONTROL_TELECONSULTATION_KEYS.DO_SMOKE]?.data,
  );

  const {inputs, setInputs} = useInput({
    selectedOption: currentAnswer?.selectedOption || '',
    selectedSmoke: currentAnswer?.selectedSmoke || '',
  });

  const medicalQuestionnaire = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);

  const questionnaires = medicalQuestionnaire?.questionnaires;
  const questionnaire = questionnaires?.find(i => i.key === BIRTH_CONTROL_TELECONSULTATION_KEYS.DO_SMOKE);

  const selectedOptionData = questionnaire?.options?.find(i => i.value === inputs.selectedOption);

  const onClickSubmit = async () => {
    dispatch(
      setQuestionnaireAnswer({
        key: BIRTH_CONTROL_TELECONSULTATION_KEYS.DO_SMOKE,
        index: questionnaire?.index || '',
        data: {...inputs, selectedSmoke: inputs.selectedOption === 'YES' ? inputs.selectedSmoke : ''},
      }),
    );
    dispatch(
      setNextQuestionnaire({
        value:
          inputs.selectedOption === 'YES'
            ? BIRTH_CONTROL_TELECONSULTATION_KEYS.HAS_SMOKED
            : BIRTH_CONTROL_TELECONSULTATION_KEYS.MEDICATION_BEING_TREATED,
      }),
    );
  };

  const renderDesc = () => {
    if (inputs.selectedOption === 'YES') {
      return (
        <UiBox w="100%" mt={spacing.small}>
          {selectedOptionData?.relations?.map((val: Relation) => {
            if (val?.elementType?.name === 'selectedOption') {
              return (
                <UiBox
                  w="100%"
                  mt={spacing.small}
                  key={val?.key || val?.elementType?.name}
                  className="animate__animated animate__fadeInLeft"
                >
                  <UiText fontSize="md" fontWeight="bold" mt={spacing.tiny}>
                    {val?.questions?.default}
                  </UiText>
                  <UiSimpleGrid columns={1} spacing={5} mt={spacing.small}>
                    {val?.options?.map(value => (
                      <UiButton
                        key={`${value.label}-${value.value}`}
                        title={value.label}
                        variant={inputs.selectedSmoke === value.value ? 'solid' : 'toggle'}
                        onClick={() => setInputs({selectedSmoke: value.value})}
                      />
                    ))}
                  </UiSimpleGrid>
                </UiBox>
              );
            }
          })}
        </UiBox>
      );
    }
    return null;
  };

  return (
    <UiQuestionnaireLayout
      actions={questionnaire?.actions}
      disableNext={!inputs.selectedOption || (inputs.selectedOption === 'YES' && !inputs.selectedSmoke)}
      handleOnNext={onClickSubmit}
      checkoutStep={questionnaire?.checkoutStep}
    >
      <Col alignItems="center" textAlign="center">
        <UiImage type="icon" name={questionnaire?.icon} boxSize={spacing.mLarge} mt={spacing.medium} />
        <UiText fontSize="xl" fontWeight="bold" mt={spacing.large}>
          {questionnaire?.questions?.default}
        </UiText>
        <UiBox w="100%" mt={spacing.small}>
          <UiSimpleGrid columns={2} spacing={5}>
            {questionnaire?.options?.map(value => (
              <ScrollToBottomButton
                key={`${value.label}-${value.value}`}
                title={value.label}
                variant={inputs.selectedOption === value.value ? 'solid' : 'toggle'}
                onClick={() => setInputs({selectedOption: value.value})}
              />
            ))}
          </UiSimpleGrid>
        </UiBox>
        {questionnaire?.descriptions?.default?.map(i => (
          <UiText key={i} fontSize="sm" mt={spacing.small} px={spacing.tiny}>
            {i}
          </UiText>
        ))}
        {renderDesc()}
      </Col>
    </UiQuestionnaireLayout>
  );
};

export default Questionnaire8Screen;
